import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate, useParams } from "react-router-dom";
import requestHandler from "../utils/requestHandler";
import {
  isHtmlNote,
  plainTextToHtml,
  noteToPlainText,
  isVisuallyEmpty,
} from "../utils/noteHtml";
import { Button, Spinner } from "./";

// Lazy so the TinyMCE bundle stays out of the main chunk
const RichTextEditor = lazy(() => import("./RichTextEditor.jsx"));

const NOTE_CHAR_LIMIT = 6000;

// The first few lessons explain that a note is what unlocks the next one;
// after that users know what's expected, so the hint goes away
const NOTE_HINT_FIRST_LESSON_ID = 7;
const NOTE_HINT_LAST_LESSON_ID = 13;

const EmptyNoteModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Overlay for the entire app */}
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />

      {/* Modal content */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        onClick={onClose}
      >
        <div
          className="bg-white dark:bg-lz-ink-mid text-lz-ink-mid dark:text-white rounded-2xl p-6 shadow-lg w-96"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-xl font-semibold mb-4">Note Required</h3>
          <p className="mb-6">Please add a note before continuing.</p>
          <div className="flex justify-end">
            <Button onClick={onClose}>OK</Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default function FinalNotes({ lessonContent, showNotes = false }) {
  const navigate = useNavigate();
  const { lessonId } = useParams();
  const [note, setNote] = useState("");
  const [showEmptyNoteModal, setShowEmptyNoteModal] = useState(false);

  useEffect(() => {
    // Legacy notes are plain text; convert so their line breaks survive
    // TinyMCE's HTML parsing instead of being silently collapsed
    const saved = lessonContent.notes || "";
    setNote(isHtmlNote(saved) ? saved : plainTextToHtml(saved));
  }, [lessonContent]);

  const visibleLength = noteToPlainText(note).length;
  const overLimit = visibleLength > NOTE_CHAR_LIMIT;

  const handleSaveNote = async (lessonContent) => {
    if (showNotes && isVisuallyEmpty(note)) {
      setShowEmptyNoteModal(true);
      return;
    }

    if (showNotes && overLimit) {
      return;
    }

    const res = await requestHandler({
      path: "lesson-complete",
      method: "POST",
      body: {
        slug: lessonId,
        notes: showNotes ? note : "",
      },
    });

    if (res.success) {
      document.activeElement?.blur();
      if (lessonContent.is_final_lesson) {
        navigate(`/degree/${lessonContent.next_degree_slug}`);
      } else {
        navigate(`/lessons/${lessonContent.next_slug}`);
      }
    } else {
      console.error("Error saving note:", res.error);
    }
  };

  if (lessonContent.id === 84) return;

  return (
    <>
      {!!showNotes && (
        <>
          <EmptyNoteModal
            isOpen={showEmptyNoteModal}
            onClose={() => setShowEmptyNoteModal(false)}
          />
          {lessonContent.id >= NOTE_HINT_FIRST_LESSON_ID &&
            lessonContent.id <= NOTE_HINT_LAST_LESSON_ID && (
              <p className="mb-2 text-xl italic text-lz-ink-mid dark:text-lz-concrete">
                To progress to the next lesson, please add your reflections in
                the box below.
              </p>
            )}
          <label htmlFor="final-notes" className="sr-only">
            Personal Journal note
          </label>
          <Suspense fallback={<Spinner />}>
            <RichTextEditor
              id="final-notes"
              value={note}
              onEditorChange={setNote}
              placeholder="Add your notes from this lesson here:"
            />
          </Suspense>
          <span
            className={`text-sm italic ${
              overLimit
                ? "text-red-600 dark:text-red-400"
                : "dark:text-lz-concrete text-lz-ink"
            }`}
          >
            {overLimit
              ? `Note exceeds the ${NOTE_CHAR_LIMIT.toLocaleString()} character limit (${visibleLength.toLocaleString()}/${NOTE_CHAR_LIMIT.toLocaleString()})`
              : `${visibleLength.toLocaleString()}/${NOTE_CHAR_LIMIT.toLocaleString()} characters`}
          </span>
        </>
      )}
      <Button
        className={`my-4 ${showNotes && overLimit ? "opacity-50 cursor-not-allowed" : ""}`}
        onClick={() => {
          handleSaveNote(lessonContent);
        }}
      >
        {lessonContent.is_final_lesson
          ? "Continue to Next Degree"
          : "Continue to Next Lesson"}
      </Button>
    </>
  );
}
